import { useEffect, useState } from "react";
import * as RouteHelper from "@utils/routes";
import { PageBreadcrumbs } from "@stories/atoms/PageBreadcrumbs/PageBreadcrumbs";
import { useProjectContext } from "@hooks/context/useProjectContext";
import { LabourHourLineItem } from "@interfaces/labourHour";
import { useLabourHour } from "@hooks/crud/useLabourHours";
import { ReportingPageTabs } from "@stories/molecules/ReportingPageTabs/ReportingPageTabs";
import { useParams } from "react-router-dom";
import { useProfileContext } from "@hooks/context/useProfileContext";
import {
  ReportStatus,
  ContractorReportLineItem,
} from "@interfaces/contractorReport";
import { useReport } from "@hooks/crud/useReports";
import { LabourHourSheet } from "@stories/organisms/LabourHourSheet/LabourHourSheet";
import { useEmployee } from "@hooks/crud/useEmployee";
import { EmployeeLineItem } from "@interfaces/employee";
import { Loader } from "@stories/atoms/Loaders/Loader/Loader";
import { StyledCard } from "@stories/atoms/StyledCard/StyledCard";
import { Grid } from "@mui/material";
import { StyledButton } from "@stories/atoms/StyledButton/StyledButton";
import PublishIcon from "@mui/icons-material/Publish";
import { MonthSelect } from "@stories/atoms/MonthSelect/MonthSelect";
import { PageLoader } from "@stories/atoms/Loaders/PageLoader/PageLoader";

export const LabourHours = () => {
  const { reportId } = useParams();

  if (!reportId) {
    throw Error("Not report number");
  }

  const { selectedProject, selectedProjectOrganisation, contractorPackage } =
    useProjectContext();
  const { userOrganisation } = useProfileContext();

  const {
    labourHours,
    isLabourHourLoading,
    deleteLabourHours,
    upsertLabourHours,
  } = useLabourHour(reportId);

  const { employees, isEmployeeLoading } = useEmployee(userOrganisation?.id);

  const [selectedReport, setSelectedReport] = useState<
    ContractorReportLineItem | undefined
  >(undefined);
  const { isReportLoading, getReport, updateReport } = useReport();

  useEffect(() => {
    const getSelectedReport = async (reportId: string) => {
      const report = await getReport(reportId);
      setSelectedReport(report);
    };
    if (reportId) {
      getSelectedReport(reportId);
    }
  }, [reportId]);

  const [employeePool, setEmployeePool] = useState<EmployeeLineItem[]>();

  useEffect(() => {
    if (!isEmployeeLoading && employees?.length > 0) {
      setEmployeePool(employees);
    }
  }, [isEmployeeLoading, employees]);

  const today = new Date();
  const [reportDate, setReportDate] = useState(
    today < selectedProject.endDate ? today : selectedProject.endDate
  );

  const handleUpsertLabourHours = (labourHour: LabourHourLineItem[]) => {
    upsertLabourHours(labourHour);
  };
  const handleDeleteLabourHours = (labourHour: LabourHourLineItem[]) => {
    deleteLabourHours(labourHour);
  };

  const submitReport = async () => {
    if (selectedReport) {
      const updatedReport = {
        ...selectedReport,
        status: ReportStatus.Complete,
      };
      await updateReport(updatedReport);
      setSelectedReport(updatedReport);
    }
  };

  const breadcrumbs = [
    {
      path: RouteHelper.contractorReports(selectedProject.id),
      name: `${selectedProject.projectName} Reports`,
    },
    {
      path: RouteHelper.labourHours(selectedProject.id, reportId),
      name: `${selectedProject.projectName} Labour Hours`,
    },
  ];

  return (
    <>
      <PageBreadcrumbs
        organisationLogo={selectedProjectOrganisation.organisationLogo}
        title={"Labour Hours"}
        links={breadcrumbs}
      />
      <StyledCard>
        <Grid container spacing={2} sx={{ padding: "20px" }}>
          <Grid item xs={12} md={8}>
            <ReportingPageTabs
              reportDate={reportDate}
              setReportDate={setReportDate}
              // submitReport={submitReport}
              reportId={reportId}
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={2}
            sx={{
              display: "flex",
              alignContent: "right",
              justifyContent: "right",
              margin: "auto",
            }}
            justifyContent={"right"}
          >
            <MonthSelect
              setDate={setReportDate}
              date={reportDate}
              minDate={selectedProject.startDate}
              maxDate={selectedProject.endDate}
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={2}
            sx={{
              display: "flex",
              alignContent: "right",
              justifyContent: "right",
              margin: "auto",
            }}
            justifyContent={"right"}
          >
            {selectedReport ? (
              <StyledButton
                variant="contained"
                color={
                  selectedReport.status === ReportStatus.Complete
                    ? "success"
                    : "secondary"
                }
                startIcon={
                  selectedReport.status ===
                  ReportStatus.Complete ? undefined : (
                    <PublishIcon />
                  )
                }
                onClick={
                  selectedReport.status === ReportStatus.Complete
                    ? undefined
                    : submitReport
                }
                disabled={
                  isEmployeeLoading || isLabourHourLoading || isReportLoading
                }
              >
                {selectedReport.status === ReportStatus.Complete
                  ? "Report Submitted"
                  : "Submit Report"}
              </StyledButton>
            ) : null}
          </Grid>

          {employeePool ? (
            <LabourHourSheet
              loading={isLabourHourLoading || isEmployeeLoading}
              reportId={reportId}
              contractorPackage={contractorPackage}
              labourHours={labourHours}
              employees={employeePool}
              handleUpsertLabourHours={handleUpsertLabourHours}
              handleDeleteLabourHours={handleDeleteLabourHours}
            />
          ) : (
            <PageLoader />
          )}
        </Grid>
      </StyledCard>
    </>
  );
};
